import { Currency, Media } from '../common'
import { Customer } from '../customer'
import { AccountIntegration } from '../accountIntegration'
import { AccountDomain } from '../accountDomain'
import { Product } from '../product'
import { FulfillmentStatus } from '../fulfillment/types'
import { StatusInfo } from '../common/Status'
import { Payment, PaymentStatus } from '../payment'
import { OrderDeliveryMethod } from '../orderDeliveryMethod'
import { Account } from '../account'
import { AccountPaymentMethod } from '../accountPaymentMethod'

/**
 * Entidad Order
 * Define la orden de compra de un cliente en el sitio web.
 */

export interface Order {
    id: string
    accountId: string
    customerId: string
    cartId?: string
    code: string
    deliveryType: OrderDeliveryType
    deliveryFirstName?: string
    deliveryLastName?: string
    deliveryAddress?: any
    deliveryPhone?: any
    pickupBranchId?: string
    accountPaymentMethodId?: string
    paymentMethodIntegrationId?: string
    billingInformation?: any
    currency: Currency
    currencySymbol?: string
    subtotalPrice: number
    totalDiscounts: number
    totalShippingPrice: number
    totalTax: number
    taxDetails?: any
    totalPrice: number
    totalRefunded: number
    status: OrderStatus
    statusInfo?: StatusInfo
    paymentStatus: OrderPaymentStatus
    paymentStatusInfo?: StatusInfo
    fulfillmentStatus: FulfillmentStatus
    statusHistory?: StatusChangeHistory[]
    customerNote?: string
    internalNote?: string
    source: OrderSource
    sourceAccountDomainId?: string
    demo: boolean
    createdAt: Date
    updatedAt: Date
    cancelledAt?: Date
    cancelReason?: string
    deletedAt?: Date
    /** Estimated delivery window start (from delivery option rules) */
    estimatedDeliveryStart?: Date
    /** Estimated delivery window end (from delivery option rules) */
    estimatedDeliveryEnd?: Date
    items?: OrderItem[]
    /** Applied promotions snapshot at order creation (checkout promos with name/code/amount). */
    promotions?: OrderAppliedPromotion[]

    customer?: Customer | null
    paymentMethodIntegration?: AccountIntegration | null
    accountDomain?: AccountDomain
    deliveryMethod?: OrderDeliveryMethod | null
    account?: Account
    accountPaymentMethod?: Partial<AccountPaymentMethod> | null
    payments?: Payment[]

    statusFlow?: StatusFlow[]
    // Estados permitidos para cambiar el estado de la orden
    statusChangeAllowed?: OrderStatus[]
    hasShipment?: boolean
}

export interface OrderItem {
    id: string
    accountId: string
    orderId: string
    productId: string
    productVariantId: string
    sku?: string
    productName: string
    variantName?: string
    currency: Currency
    currencySymbol?: string
    unitPrice: number
    totalDiscount: number
    totalPrice: number
    quantity: number
    quantityFulfilled: number
    quantityRefunded: number
    quantityReturned: number
    totalTax: number
    taxName?: string
    createdAt: Date
    updatedAt: Date

    // Snapshot del producto en el momento de la compra
    productSnapshot?: OrderItemSnapshot

    product?: Product
}

export enum OrderStatus {
    PENDING = 'PENDING', // Order placed, awaiting payment confirmation
    CONFIRMED = 'CONFIRMED', // Payment received, order confirmed
    PROCESSING = 'PROCESSING', // Order being prepared
    PROCESSED = 'PROCESSED', // Order ready to be shipped
    ON_HOLD = 'ON_HOLD', // Order temporarily paused
    COMPLETED = 'COMPLETED', // Order finished (e.g., after return period)
    CANCELLED = 'CANCELLED', // Order cancelled before fulfillment
    FAILED = 'FAILED', // Order failed (e.g., payment failed irrecoverably)
}

export enum OrderPaymentStatus {
    PENDING = 'PENDING',
    PARTIAL = 'PARTIAL',
    PAID = 'PAID',
    OVERPAID = 'OVERPAID',
    REFUNDED = 'REFUNDED',
    PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
}

export enum OrderSource {
    WEB = 'WEB',
    POS = 'POS',
    API = 'API',
}

export enum OrderDeliveryType {
    SHIPPING = 'SHIPPING',
    PICKUP = 'PICKUP',
}

/**
 * Pseudo-estado amigable para la vista/filtro de órdenes en backoffice.
 * Cada valor se deriva de combinaciones de status, paymentStatus, fulfillmentStatus y deliveryType.
 */
export enum DisplayOrderStatus {
    PENDIENTES_DE_PAGO = 'PENDIENTES_DE_PAGO',
    EN_PROCESO = 'EN_PROCESO',
    LISTAS_PARA_ENVIAR = 'LISTAS_PARA_ENVIAR',
    LISTAS_PARA_RETIRAR = 'LISTAS_PARA_RETIRAR',
    ENVIADAS = 'ENVIADAS',
    ENTREGADAS = 'ENTREGADAS',
    EN_ESPERA = 'EN_ESPERA',
    CON_PROBLEMAS = 'CON_PROBLEMAS',
    CANCELADAS = 'CANCELADAS',
    REEMBOLSADAS = 'REEMBOLSADAS',
}

export interface StatusChangeHistory {
    type: 'order' | 'fulfillment' | 'payment'
    status: OrderStatus | FulfillmentStatus | PaymentStatus
    timestamp: Date
    reason?: string
    userId?: string
    metadata?: Record<string, any>
}

export interface OrderItemSnapshot {
    sku?: string
    productName: string
    variantName?: string
    media?: Media[]
}

/** Promotion applied to an order (snapshot at checkout time). */
export interface OrderAppliedPromotion {
    code: string
    amount: number
    name?: string
    description?: string
    isAutomatic?: boolean
}

type StatusByType = {
    order: OrderStatus
    fulfillment: FulfillmentStatus
    payment: PaymentStatus
}

export type StatusFlow<T extends keyof StatusByType = keyof StatusByType> = {
    type: T
    status: StatusByType[T]
    text?: string
    doneAt: Date | null
}

export type NextStatusAction<T extends keyof StatusByType = keyof StatusByType> = {
    type: T
    status: StatusByType[T]
    text: string
    fulfillmentId?: string // for fulfillment status updates
}
